import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ShieldCheck, Loader2, KeyRound, Mail } from "lucide-react";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [mode, setMode] = useState("email"); // email | code
  const [email, setEmail] = useState("urbanvisionmag3908@gmail.com");
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const u = await login(mode === "code" ? { code } : { email, password });
      navigate(u?.role === "agent" ? "/clients" : "/");
    } catch (err) {
      setError(apiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 relative z-10">
      <div className="flex items-center justify-center p-6 sm:p-12">
        <div className="w-full max-w-sm animate-fade-up">
          <div className="flex items-center gap-2.5 mb-10">
            <div className="h-10 w-10 rounded-lg bg-primary flex items-center justify-center">
              <ShieldCheck className="h-6 w-6 text-white" />
            </div>
            <div>
              <div className="font-display font-bold text-lg leading-none tracking-tight">Urbanvision</div>
              <div className="text-[10px] tracking-[0.2em] uppercase text-muted-foreground mt-0.5">Financial Solutions</div>
            </div>
          </div>
          <h1 className="font-display text-4xl font-bold tracking-tight leading-none">Welcome back</h1>
          <p className="text-muted-foreground mt-3 text-sm">Sign in to your credit & business command center.</p>

          <div className="flex gap-2 mt-6 p-1 rounded-lg bg-secondary w-fit">
            <button onClick={() => setMode("email")} className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${mode === "email" ? "bg-primary text-white" : "text-muted-foreground"}`} data-testid="mode-email">
              <Mail className="h-3.5 w-3.5" /> Email
            </button>
            <button onClick={() => setMode("code")} className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${mode === "code" ? "bg-primary text-white" : "text-muted-foreground"}`} data-testid="mode-code">
              <KeyRound className="h-3.5 w-3.5" /> Login Code
            </button>
          </div>

          <form onSubmit={submit} className="mt-5 space-y-4" data-testid="login-form">
            {mode === "email" ? (
              <>
                <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>
                  <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="login-email" required />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="password">Password</Label>
                  <Input id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} data-testid="login-password" placeholder="••••••••" required />
                </div>
              </>
            ) : (
              <div className="space-y-2">
                <Label htmlFor="code">Access Code</Label>
                <Input id="code" inputMode="numeric" value={code} onChange={(e) => setCode(e.target.value)} data-testid="login-code" placeholder="••••" className="tracking-[0.5em] text-center text-lg font-mono" required />
              </div>
            )}
            {error && <p className="text-sm text-destructive" data-testid="login-error">{error}</p>}
            <Button type="submit" className="w-full rounded-lg" disabled={loading} data-testid="login-submit">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Sign in"}
            </Button>
          </form>
        </div>
      </div>
      <div className="hidden lg:block relative border-l border-border overflow-hidden">
        <img src="https://images.unsplash.com/photo-1624213012413-fda54df1810f?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400" alt="Corporate" className="absolute inset-0 w-full h-full object-cover opacity-40" />
        <div className="absolute inset-0 bg-gradient-to-t from-background via-background/50 to-transparent" />
        <div className="absolute bottom-12 left-12 right-12">
          <h2 className="font-display text-3xl font-bold leading-tight">Clean credit. Unlock approvals. Build your empire.</h2>
          <p className="text-muted-foreground mt-3">Upload a credit report, auto-generate dispute letters for all three bureaus, see what you qualify for, and set up your corporation.</p>
        </div>
      </div>
    </div>
  );
}
